import { format } from 'date-fns';
import { ApiFilterRequest, ApiFiltersRequest, ApiQueryParamsFilterRequest } from '../../table';

export class CommonUtils {
    private static _queryFilterParams: ApiQueryParamsFilterRequest;
    private static _filters: ApiFilterRequest[] = [];

    public static resetFilters() {
        this._filters = [];
    }

    public static setQueryFilter(filter: ApiFiltersRequest): ApiQueryParamsFilterRequest {
        this._updateFilters(filter.filters);
        this._queryFilterParams = this._buildQueryParams(filter);
        return this._queryFilterParams;
    }

    private static _updateFilters(newFilters: ApiFilterRequest[]): void {
        // If no filters are passed, clear all existing filters
        if (!newFilters || newFilters.length === 0) {
            this._filters = [];
            return;
        }

        // Collect fields from new filters
        const newFilterFields = new Set(newFilters.map((f) => f.field));

        // Remove filters that are not in the new filters array
        this._filters = this._filters.filter((existingFilter) => newFilterFields.has(existingFilter.field));

        newFilters.forEach((newFilter) => {
            const existingFilterIndex = this._filters.findIndex((f) => f.field === newFilter.field);

            if (this._isFilterValueEmpty(newFilter.value)) {
                this._removeFilter(existingFilterIndex);
            } else {
                this._updateOrAddFilter(existingFilterIndex, newFilter);
            }
        });
    }

    private static _isFilterValueEmpty(value: any): boolean {
        return value === null || (Array.isArray(value) && value.length === 0);
    }

    private static _removeFilter(index: number): void {
        if (index !== -1) {
            this._filters.splice(index, 1);
        }
    }

    private static _updateOrAddFilter(index: number, newFilter: ApiFilterRequest): void {
        if (index !== -1) {
            this._filters[index] = newFilter;
        } else {
            this._filters.push(newFilter);
        }
    }

    private static _buildQueryParams(filter: ApiFiltersRequest): ApiQueryParamsFilterRequest {
        return {
            filters: this._filters.flatMap((f) => this._formatFilter(f)),
            page: filter.page?.pageNumber || 0,
            size: filter.page?.pageSize || 10,
            sort: filter.page?.sort?.map((s) => `${s.field},${s.direction.toLowerCase()}`) || []
        };
    }

    private static _formatFilter(filter: ApiFilterRequest): string[] | string {
        let value = filter.value;
        if (filter.typeValue === 'Date') {
            value = format(new Date(value), 'yyyy-MM-dd');
        } else if (filter.typeValue === 'Date-Range' && Array.isArray(value)) {
            value = value.map((date) => format(new Date(date), 'yyyy-MM-dd')).join(',');
        } else if (filter.typeValue === 'DateTime-Range' && Array.isArray(value)) {
            // Normalize dates to cover full days
            const dates = value.map((date, index) => {
                const d = new Date(date);
                if (index === 0) {
                    // Start date: set to beginning of day (00:00:00)
                    d.setHours(0, 0, 0, 0);
                } else {
                    // End date: set to end of day (23:59:59)
                    d.setHours(23, 59, 59, 999);
                }
                return format(d, "yyyy-MM-dd'T'HH:mm:ss");
            });
            value = dates.join('|');
        }

        if (Array.isArray(value)) {
            return value.map((v) => `${filter.field}::${filter.operator}::${v}`);
        }
        return [`${filter.field}::${filter.operator}::${value}`];
    }
}
